import React, { useEffect, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FaEdit, FaCopy, FaTimes } from 'react-icons/fa';
import { observer } from 'mobx-react-lite';
import { ListingType } from '@Encore-FastStartup-Phase-Out/encore-shared';

import { ActiveListing } from '../../constants/types';
import { Colors } from '../../theme/colors';
import { useStore } from '../../hooks/useStore';
import DetailDrawer from '../drawer/DetailDrawer';
import ActiveListingDetails from '../drawer/ActiveListingDetails';

interface FlashListingScheduleProps {
  listings: ActiveListing[];
  onReorder: (reorderedListings: ActiveListing[]) => void;
  fetchListings?: () => void;
}

interface SortableItemProps {
  listing: ActiveListing;
  duration: number;
  height: number;
  rowSpan: number;
  borderColor: string;
  getListingTypeDisplay: (listingType: ListingType) => string;
  onDurationSave: (newDuration: number) => void;
  onMarketValueSave: (newMarketValue: number) => void;
  onViewDetails: () => void;
  onSnipePriceSave: (value: number | null) => void;
  onSnipePriceDelete?: () => void;
}

const SortableItem = ({
  listing,
  duration,
  height,
  rowSpan,
  getListingTypeDisplay,
  onDurationSave,
  onMarketValueSave,
  onViewDetails,
  onSnipePriceSave, // NEW
  onSnipePriceDelete, // NEW (optional)
}: SortableItemProps) => {
  const [localDuration, setLocalDuration] = useState(duration);
  const [localMarketValue, setLocalMarketValue] = useState(
    (listing.marketPrice || 0).toString(),
  );
  const [localSnipePrice, setLocalSnipePrice] = useState(
    String(listing.snipePrice ?? 0),
  );

  const toast = useToast();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: listing.id });

  const rowBg = useColorModeValue(
    Colors.light.background,
    Colors.dark.background,
  );
  const hoverBg = useColorModeValue(Colors.light.gray3, Colors.dark.gray3);
  const textColor = useColorModeValue(Colors.light.text, Colors.dark.text);
  const primaryColor = useColorModeValue(
    Colors.light.primary,
    Colors.dark.primary,
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useEffect(() => {
    setLocalDuration(duration);
  }, [duration]);

  useEffect(() => {
    setLocalMarketValue((listing.marketPrice || 0).toString());
  }, [listing.marketPrice]);

  useEffect(() => {
    setLocalSnipePrice(String(listing.snipePrice ?? 0));
  }, [listing.snipePrice]);

  const normalizedListingSnipeDisplay = Number(listing.snipePrice ?? 0);
  const normalizedLocalSnipeDisplay = Number(localSnipePrice) || 0;
  const snipeUnchanged =
    normalizedLocalSnipeDisplay === normalizedListingSnipeDisplay;

  const handleSnipeSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    const n = Number(localSnipePrice);
    const toSave = !isFinite(n) || n === 0 ? null : n; // 0 => null
    onSnipePriceSave?.(toSave);
  };

  const handleSnipeClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalSnipePrice('0'); // reflect “not set”
    // Prefer dedicated delete if provided; otherwise save null
    if (onSnipePriceDelete) onSnipePriceDelete();
    else onSnipePriceSave?.(null);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to drag handlers
    onDurationSave(localDuration);
  };

  const handleMarketValueSave = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to drag handlers
    const numericValue = parseFloat(localMarketValue) || 0;
    onMarketValueSave(numericValue);
  };

  const handleCopyTitle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to drag handlers
    try {
      await navigator.clipboard.writeText(listing.title);
      toast({
        title: 'Title copied!',
        description: 'Listing title has been copied to clipboard.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Failed to copy title:', err);
      toast({
        title: 'Copy failed',
        description: 'Failed to copy title to clipboard.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Tr
      ref={setNodeRef}
      style={style}
      bg={rowBg}
      border="1px"
      borderColor={isDragging ? 'blue.300' : 'transparent'}
      shadow={isDragging ? 'lg' : 'none'}
      _hover={{ bg: hoverBg }}
      userSelect="none"
      height={`${height}px`}
    >
      <Td
        rowSpan={rowSpan}
        {...attributes}
        {...listeners}
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
      >
        <Text fontSize="sm" fontWeight="medium" color={textColor}>
          {listing.user.userName}
        </Text>
      </Td>

      <Td rowSpan={rowSpan}>
        <HStack spacing={2} align="center">
          <Text fontSize="sm" noOfLines={2} maxW="200px" color={textColor}>
            {listing.title}
          </Text>
          <IconButton
            aria-label="Copy title to clipboard"
            icon={<FaCopy />}
            size="xs"
            variant="ghost"
            color={textColor}
            onClick={handleCopyTitle}
            _hover={{ bg: primaryColor, color: 'white' }}
          />
        </HStack>
      </Td>

      <Td rowSpan={rowSpan}>
        <Text fontSize="sm" color={textColor}>
          {listing.maxBids && listing.maxBids.length >= 2
            ? `$${listing.maxBids[0].maxBid}, ${listing.maxBids[1].maxBid}`
            : listing.maxBids && listing.maxBids.length === 1
              ? `$${listing.maxBids[0].maxBid}`
              : 'N/A'}
        </Text>
      </Td>

      <Td rowSpan={rowSpan}>
        <HStack spacing={2}>
          <Select
            size="sm"
            value={localDuration}
            onChange={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              setLocalDuration(Number(e.target.value));
            }}
            width="80px"
          >
            <option value={30}>30s</option>
            <option value={60}>60s</option>
            <option value={90}>90s</option>
            <option value={120}>120s</option>
            <option value={150}>150s</option>
            <option value={180}>180s</option>
            <option value={210}>210s</option>
            <option value={240}>240s</option>
            <option value={270}>270s</option>
            <option value={300}>300s</option>
          </Select>
          <Button
            size="xs"
            bg={primaryColor}
            onClick={handleSave}
            isDisabled={isDragging || localDuration === duration}
          >
            Save
          </Button>
        </HStack>
      </Td>

      <Td rowSpan={rowSpan}>
        <Text fontSize="sm" color={textColor}>
          {new Date(Number(listing.startTime)).toLocaleString()}
        </Text>
      </Td>

      <Td rowSpan={rowSpan}>
        <HStack spacing={2}>
          <Input
            size="sm"
            type="number"
            value={localMarketValue}
            onChange={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              setLocalMarketValue(e.target.value);
            }}
            onBlur={(e) => {
              if (e.target.value === '' || e.target.value === undefined) {
                setLocalMarketValue('0');
              }
            }}
            width="100px"
            min={0}
            step={0.01}
          />
          <Button
            size="xs"
            bg={primaryColor}
            onClick={handleMarketValueSave}
            isDisabled={
              isDragging ||
              parseFloat(localMarketValue) === (listing.marketPrice || 0)
            }
          >
            Save
          </Button>
        </HStack>
      </Td>

      <Td rowSpan={rowSpan}>
        <HStack spacing={2}>
          <Input
            size="sm"
            type="number"
            value={localSnipePrice}
            onChange={(e) => {
              e.stopPropagation();
              setLocalSnipePrice(e.target.value);
            }}
            onBlur={(e) => {
              if (e.target.value === '' || e.target.value === undefined) {
                setLocalSnipePrice('0'); // show 0 = “not set”
              }
            }}
            width="100px"
            min={0}
            step={0.01}
            placeholder="Snipe price"
          />
          <Button
            size="xs"
            bg={primaryColor}
            onClick={handleSnipeSave}
            isDisabled={isDragging || snipeUnchanged}
          >
            Save
          </Button>
          <IconButton
            aria-label="Clear snipe price"
            icon={<FaTimes />}
            size="xs"
            variant="outline"
            onClick={handleSnipeClear}
          />
        </HStack>
      </Td>

      <Td rowSpan={rowSpan}>
        <Text fontSize="sm" color={textColor}>
          {getListingTypeDisplay(listing.listingType)}
        </Text>
      </Td>

      <Td rowSpan={rowSpan}>
        <IconButton
          aria-label="Edit listing details"
          icon={<FaEdit />}
          size="xs"
          variant="solid"
          color={primaryColor}
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails();
          }}
        />
      </Td>
    </Tr>
  );
};

const FlashListingSchedule: React.FC<FlashListingScheduleProps> = ({
  listings,
  onReorder,
  fetchListings,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const { listingStore } = useStore();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleViewDetails = (listing: ActiveListing) => {
    listingStore.setDisplayedActiveListing(listing);
    onOpen();
  };

  const bgColor = useColorModeValue(
    Colors.light.background,
    Colors.dark.background,
  );
  const borderColor = useColorModeValue(Colors.light.gray, Colors.dark.gray);
  const timelineBg = useColorModeValue(Colors.light.gray, Colors.dark.gray);
  const headerTextColor = useColorModeValue(
    Colors.light.text,
    Colors.dark.text,
  );
  const tableBg = useColorModeValue(
    Colors.light.background,
    Colors.dark.background,
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Calculate duration in seconds for each listing
  const calculateDuration = (startTime?: string, endTime?: string): number => {
    if (!startTime || !endTime) return 30; // Default to 30 seconds

    const start = new Date(Number(startTime)).getTime();
    const end = new Date(Number(endTime)).getTime();
    return Math.max((end - start) / 1000, 30); // Minimum 30 seconds
  };

  // Common function to adjust subsequent listings to prevent overlaps
  const adjustSubsequentListings = (
    listings: ActiveListing[],
    startIndex: number,
    currentEndTime: number,
  ): ActiveListing[] => {
    let updatedListings = [...listings];
    let runningEndTime = currentEndTime;

    for (let i = startIndex; i < updatedListings.length; i++) {
      const listing = updatedListings[i];
      const listingDuration = calculateDuration(
        listing.startTime,
        listing.endTime,
      );

      // Always ensure this listing starts at or after the previous one ends
      const adjustedStartTime = new Date(runningEndTime);
      const adjustedEndTime = new Date(runningEndTime + listingDuration * 1000);

      updatedListings[i] = {
        ...listing,
        startTime: adjustedStartTime.getTime().toString(),
        endTime: adjustedEndTime.getTime().toString(),
      };

      runningEndTime = adjustedEndTime.getTime();
    }

    return updatedListings;
  };

  const getListingHeight = (duration: number) => {
    // Base height of 60px, with additional height based on duration
    // Each 30 seconds adds ~60px
    return Math.max(60, (duration / 30) * 60);
  };

  const getListingTypeDisplay = (listingType: ListingType) => {
    switch (listingType) {
      case ListingType.GIVEAWAY:
        return 'Giveaway';
      case ListingType.AUCTION:
        return 'Auction';
      default:
        return '';
    }
  };

  // Generate timeline data with 30-second intervals and position listings by actual start time
  const timelineData = useMemo(() => {
    if (listings.length === 0)
      return {
        timeSlots: [],
        listingPositions: new Map(),
        slotOverlapCounts: new Map(),
      };

    // Sort listings by start time
    const sortedListings = [...listings].sort((a, b) => {
      if (!a.startTime || !b.startTime) return 0;
      return (
        new Date(Number(a.startTime)).getTime() -
        new Date(Number(b.startTime)).getTime()
      );
    });

    const firstListing = sortedListings[0];
    const lastListing = sortedListings[sortedListings.length - 1];

    if (!firstListing.startTime || !lastListing.endTime) {
      return {
        timeSlots: [],
        listingPositions: new Map(),
        slotOverlapCounts: new Map(),
      };
    }

    const startTime = new Date(Number(firstListing.startTime));
    const endTime = new Date(Number(lastListing.endTime));
    const totalDuration = (endTime.getTime() - startTime.getTime()) / 1000;

    // Generate time slots every 30 seconds
    const timeSlots: {
      timestamp: string;
      time: Date;
      slotIndex: number;
      overlapCount: number;
    }[] = [];
    for (let i = 0; i <= totalDuration; i += 30) {
      const slotTime = new Date(startTime.getTime() + i * 1000);
      timeSlots.push({
        timestamp: slotTime.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
        time: slotTime,
        slotIndex: i / 30,
        overlapCount: 0,
      });
    }

    // Calculate which time slot each listing should appear in based on actual start time
    const listingPositions = new Map<
      string,
      { startSlot: number; spanSlots: number; rowIndex: number }
    >();

    // Track overlap counts for each slot
    const slotOverlapCounts = new Map<number, number>();
    const slotActiveListings = new Map<number, string[]>();

    sortedListings.forEach((listing) => {
      if (listing.startTime) {
        const listingStart = new Date(Number(listing.startTime));
        const timeDiff = (listingStart.getTime() - startTime.getTime()) / 1000;
        const startSlot = Math.floor(timeDiff / 30);
        const duration = calculateDuration(listing.startTime, listing.endTime);
        const spanSlots = Math.max(1, Math.ceil(duration / 30)); // At least 1 slot, rounded up

        // Find which row this listing should be in for each slot it spans
        let rowIndex = 0;
        for (let slot = startSlot; slot < startSlot + spanSlots; slot++) {
          const activeInSlot = slotActiveListings.get(slot) || [];
          // Find the first available row
          let availableRow = 0;
          while (activeInSlot.includes(`row-${availableRow}`)) {
            availableRow++;
          }
          rowIndex = Math.max(rowIndex, availableRow);
        }

        // Mark this listing as occupying this row in all its slots
        for (let slot = startSlot; slot < startSlot + spanSlots; slot++) {
          const activeInSlot = slotActiveListings.get(slot) || [];
          activeInSlot.push(`row-${rowIndex}`);
          slotActiveListings.set(slot, activeInSlot);

          const currentCount = slotOverlapCounts.get(slot) || 0;
          slotOverlapCounts.set(slot, Math.max(currentCount, rowIndex + 1));
        }

        listingPositions.set(listing.id, { startSlot, spanSlots, rowIndex });
      }
    });

    // Update timeSlots with overlap counts
    timeSlots.forEach((slot) => {
      slot.overlapCount = slotOverlapCounts.get(slot.slotIndex) || 1;
    });

    return { timeSlots, listingPositions, slotOverlapCounts };
  }, [listings]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const draggedListing = listings.find((item) => item.id === active.id);
      const targetListing = listings.find((item) => item.id === over?.id);

      if (draggedListing && targetListing) {
        // First, reorder the array to get the new order
        const oldIndex = listings.findIndex((item) => item.id === active.id);
        const newIndex = listings.findIndex((item) => item.id === over?.id);
        const reorderedListings = arrayMove(listings, oldIndex, newIndex);

        let currentTime = new Date(Number(listings[0].startTime));

        const finalListings = reorderedListings.map((listing, index) => {
          const listingDuration = calculateDuration(
            listing.startTime,
            listing.endTime,
          );

          const adjustedStartTime = new Date(currentTime);
          const adjustedEndTime = new Date(
            currentTime.getTime() + listingDuration * 1000,
          );

          currentTime = adjustedEndTime;

          return {
            ...listing,
            startTime: adjustedStartTime.getTime().toString(),
            endTime: adjustedEndTime.getTime().toString(),
          };
        });

        onReorder(finalListings);
      } else {
        // Fallback to simple reordering if we can't find the listings
        const oldIndex = listings.findIndex((item) => item.id === active.id);
        const newIndex = listings.findIndex((item) => item.id === over?.id);
        const newListings = arrayMove(listings, oldIndex, newIndex);
        onReorder(newListings);
      }
    }

    setActiveId(null);
  };

  const handleRemoveEmptySlot = (slotTime: Date) => {
    // Calculate the time of this empty slot
    const emptySlotTime = slotTime.getTime();

    // Update all listings that start after this empty slot
    const updatedListings = listings.map((listing) => {
      if (listing.startTime && listing.endTime) {
        const listingStartTime = new Date(Number(listing.startTime)).getTime();

        // If listing starts after the empty slot, move it up by 30 seconds
        if (listingStartTime > emptySlotTime) {
          const newStartTime = listingStartTime - 30000; // 30 seconds in milliseconds
          const newEndTime =
            new Date(Number(listing.endTime)).getTime() - 30000;

          return {
            ...listing,
            startTime: newStartTime.toString(),
            endTime: newEndTime.toString(),
          };
        }
      }
      return listing;
    });

    // Always call the callback, even if no listings were moved (for last empty slot)
    onReorder(updatedListings);
  };

  const handleDurationSave = (listingId: string, newDuration: number) => {
    // Find the listing being updated
    const listingIndex = listings.findIndex(
      (listing) => listing.id === listingId,
    );

    if (listingIndex === -1) return;

    const updatedListing = listings[listingIndex];

    // Calculate the new end time based on the new duration
    const startTime = new Date(Number(updatedListing.startTime));
    const newEndTime = new Date(startTime.getTime() + newDuration * 1000);

    // Update the current listing with new duration
    const listingsWithUpdatedDuration = listings.map((listing) => {
      if (listing.id === listingId) {
        return {
          ...listing,
          endTime: newEndTime.getTime().toString(),
        };
      }
      return listing;
    });

    // Adjust subsequent listings using the common function
    const finalListings = adjustSubsequentListings(
      listingsWithUpdatedDuration,
      listingIndex + 1,
      newEndTime.getTime(),
    );

    // Update the entire schedule using onReorder
    onReorder(finalListings);
  };

  const handleMarketValueSave = (listingId: string, newMarketValue: number) => {
    // Update the listing with new market value
    const updatedListings = listings.map((listing) => {
      if (listing.id === listingId) {
        return {
          ...listing,
          marketPrice: newMarketValue,
        };
      }
      return listing;
    });

    listingStore.updateFlashListingSchedule(updatedListings);
  };

  const handleSnipePriceSave = (listingId: string, value: number | null) => {
    // Update the listing with new snipe price
    const updatedListings = listings.map((listing) => {
      if (listing.id === listingId) {
        return {
          ...listing,
          snipePrice: value,
        };
      }
      return listing;
    });

    listingStore.updateFlashListingSchedule(updatedListings);
  };

  if (listings.length === 0) {
    return (
      <Box
        w="100%"
        h="90%"
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="gray.500">No flash listings available</Text>
      </Box>
    );
  }

  const activeListing = activeId
    ? listings.find((listing) => listing.id === activeId)
    : null;

  return (
    <Box
      w="100%"
      h="90%"
      border="1px"
      borderColor={borderColor}
      borderRadius="md"
      overflow="hidden"
    >
      <Flex h="100%">
        {/* Timeline Column */}
        <Box
          w="120px"
          bg={timelineBg}
          borderRight="1px"
          borderColor={borderColor}
          overflowY="auto"
        >
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th color={headerTextColor} fontSize="sm" p={1}>
                  Timeline
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {timelineData.timeSlots.map((slot, index) => {
                // Calculate the height based on listings that START in this slot only
                const listingsStartingInSlot = listings.filter((listing) => {
                  const position = timelineData.listingPositions.get(
                    listing.id,
                  );
                  if (!position) return false;
                  return position.startSlot === index;
                });

                // Calculate the number of listings active during this time slot
                const listingsActiveInSlot = listings.filter((listing) => {
                  const position = timelineData.listingPositions.get(
                    listing.id,
                  );
                  if (!position) return false;
                  const { startSlot, spanSlots } = position;
                  return index >= startSlot && index < startSlot + spanSlots;
                });

                // Timeline height should be 60px * number of active listings
                const slotHeight = Math.max(
                  60,
                  listingsActiveInSlot.length * 60,
                );

                return (
                  <Tr key={index} height={`${slotHeight}px`}>
                    <Td p={2} verticalAlign="top" height={`${slotHeight}px`}>
                      <Text
                        fontSize="sm"
                        fontFamily="mono"
                        color={headerTextColor}
                      >
                        {slot.timestamp}
                      </Text>
                      {listingsStartingInSlot.length > 1 && (
                        <Text
                          fontSize="xs"
                          color="orange.500"
                          fontWeight="bold"
                          mt={1}
                        >
                          {listingsStartingInSlot.length} starting
                        </Text>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        {/* Listings Column */}
        <Box flex={1} bg={bgColor} overflowY="auto" position="relative">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={listings.map((l) => l.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table variant="simple" size="sm" bg={tableBg}>
                <Thead>
                  <Tr>
                    <Th color={headerTextColor}>Seller Username</Th>
                    <Th color={headerTextColor}>Title</Th>
                    <Th color={headerTextColor}>Top PreBids</Th>
                    <Th color={headerTextColor}>Duration</Th>
                    <Th color={headerTextColor}>Start Time</Th>
                    <Th color={headerTextColor}>Market Value</Th>
                    <Th color={headerTextColor}>Snipe Price</Th>
                    <Th color={headerTextColor}>Listing Type</Th>
                    <Th color={headerTextColor}>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {timelineData.timeSlots
                    .map((slot, slotIndex) => {
                      // Find listings that START in this time slot only
                      const listingsStartingInSlot = listings.filter(
                        (listing) => {
                          const position = timelineData.listingPositions.get(
                            listing.id,
                          );
                          return position && position.startSlot === slotIndex;
                        },
                      );

                      // Check if ANY listings are active during this slot (starting or continuing)
                      const listingsActiveInSlot = listings.filter(
                        (listing) => {
                          const position = timelineData.listingPositions.get(
                            listing.id,
                          );
                          if (!position) return false;
                          const { startSlot, spanSlots } = position;
                          return (
                            slotIndex >= startSlot &&
                            slotIndex < startSlot + spanSlots
                          );
                        },
                      );

                      if (listingsStartingInSlot.length > 0) {
                        // Create rows only for listings that start in this slot
                        const rows: JSX.Element[] = [];

                        listingsStartingInSlot.forEach((listing) => {
                          const duration = calculateDuration(
                            listing.startTime,
                            listing.endTime,
                          );
                          const calculatedHeight = getListingHeight(duration);

                          rows.push(
                            <SortableItem
                              key={listing.id}
                              listing={listing}
                              duration={duration}
                              height={calculatedHeight}
                              rowSpan={1}
                              borderColor={borderColor}
                              getListingTypeDisplay={getListingTypeDisplay}
                              onDurationSave={(newDuration) =>
                                handleDurationSave(listing.id, newDuration)
                              }
                              onMarketValueSave={(newMarketValue) =>
                                handleMarketValueSave(
                                  listing.id,
                                  newMarketValue,
                                )
                              }
                              onViewDetails={() => handleViewDetails(listing)}
                              onSnipePriceSave={(value) =>
                                handleSnipePriceSave(listing.id, value)
                              }
                            />,
                          );
                        });

                        return rows;
                      } else if (listingsActiveInSlot.length === 0) {
                        // Truly empty time slot - no listings active at all
                        return (
                          <Tr
                            key={`empty-${slotIndex}`}
                            height="60px"
                            onClick={() => handleRemoveEmptySlot(slot.time)}
                            cursor="pointer"
                            _hover={{ bg: 'gray.600' }}
                            title="Click to remove this empty slot and move all subsequent events up by 30 seconds"
                          >
                            <Td colSpan={8} p={2} height="60px">
                              <Text
                                fontSize="xs"
                                color="gray.400"
                                textAlign="center"
                              >
                                Empty slot - Click to remove
                              </Text>
                            </Td>
                          </Tr>
                        );
                      } else {
                        // Listings are active but none start here - don't create any rows
                        return null;
                      }
                    })
                    .filter(Boolean)}
                </Tbody>
              </Table>
            </SortableContext>

            <DragOverlay>
              {activeId && activeListing ? (
                <Table variant="simple" size="sm">
                  <Tbody>
                    <SortableItem
                      listing={activeListing}
                      duration={calculateDuration(
                        activeListing.startTime,
                        activeListing.endTime,
                      )}
                      height={getListingHeight(
                        calculateDuration(
                          activeListing.startTime,
                          activeListing.endTime,
                        ),
                      )}
                      rowSpan={1}
                      borderColor={borderColor}
                      getListingTypeDisplay={getListingTypeDisplay}
                      onDurationSave={() => {}}
                      onMarketValueSave={() => {}}
                      onViewDetails={() => {}}
                      onSnipePriceSave={(value) => {}}
                    />
                  </Tbody>
                </Table>
              ) : null}
            </DragOverlay>
          </DndContext>
        </Box>
      </Flex>

      <DetailDrawer isOpen={isOpen} onClose={onClose}>
        <ActiveListingDetails postSaveAction={fetchListings} />
      </DetailDrawer>
    </Box>
  );
};

export default observer(FlashListingSchedule);
